<script setup lang="ts">
import dayjs from "dayjs";

/**
 * Where a tab is between its twelve months and one month by day: the tab's
 * name, the node or screen being edited, the plan's year, and the month when
 * one is open.
 *
 * The first two crumbs look the same whichever view is open, so switching
 * views never restyles them. The tab's name quietly becomes a way back once a
 * month is open; the node is context only and never a link — it is changed in
 * the hierarchy, not here. The year is the one visible button, and the level
 * you are on is plain bold text, so what can be clicked never reads like
 * where you already are.
 */
const props = defineProps<{
	label: string;
	/** The hierarchy node or screen the tab is editing. */
	targetName: string;
	year: number | undefined;
	/** 1-12 when a month is open, null for the twelve months. */
	month: number | null;
}>();

const emit = defineEmits<{ back: [] }>();

const monthName = (month: number) =>
	dayjs(new Date(props.year ?? dayjs().year(), month - 1, 1)).format("MMMM");
</script>

<template>
	<nav class="breadcrumb d-flex align-center ga-1 text-body-2" :aria-label="`${label} level`">
		<button
			v-if="month !== null"
			class="crumb-back text-medium-emphasis"
			title="Back to the twelve months"
			@click="emit('back')"
		>
			{{ label }}
		</button>
		<span v-else class="text-medium-emphasis">{{ label }}</span>
		<v-icon icon="fa-light fa-chevron-right" size="10" class="text-medium-emphasis mx-1" />
		<span class="text-medium-emphasis">{{ targetName }}</span>
		<v-icon icon="fa-light fa-chevron-right" size="10" class="text-medium-emphasis mx-1" />

		<template v-if="month !== null">
			<v-btn
				size="small"
				variant="tonal"
				color="primary"
				title="Back to the twelve months"
				@click="emit('back')"
			>
				{{ year }}
			</v-btn>
			<v-icon icon="fa-light fa-chevron-right" size="10" class="text-medium-emphasis mx-1" />
			<span class="font-weight-bold">{{ monthName(month) }}</span>
		</template>
		<span v-else class="font-weight-bold">{{ year }}</span>
	</nav>
</template>

<style scoped lang="scss">
// As tall as the year's button, so the row keeps its height when it appears.
.breadcrumb {
	min-height: 28px;
}

// Reads exactly like the plain label it is in the month view.
.crumb-back {
	border: 0;
	background: transparent;
	padding: 0;
	font: inherit;
	cursor: pointer;
}
</style>
